/** @format */

import { lazyWithRetry } from '@/shared/lib/lazy-with-retry'
import {
  Branches,
  Category,
  Company,
  Customers,
  Departments,
  Districts,
  Employees,
  GlassThickness,
  Home,
  Lables,
  Materials,
  Measurement,
  MinMaxPercentage,
  Positions,
  Regions,
  Roles,
  Signatories,
  Warehouse,
} from 'pages'
import { useTranslation } from 'react-i18next'

// Lazy-loaded feature components for code splitting
// -- DataLibrary
const EquipmentsComponent = lazyWithRetry(() => import('@/features/equipments'))
const LenghtHeightComponent = lazyWithRetry(() => import('@/features/length-height'))
const CategoryOrderComponent = lazyWithRetry(() => import('@/features/category-order'))
const DoctypesComponent = lazyWithRetry(() => import('@/features/doc-types'))
const TransportsComponent = lazyWithRetry(() => import('@/features/transports'))
const ReadyProductsComponent = lazyWithRetry(() => import('@/features/ready-products'))
const WindowDesigner = lazyWithRetry(() => import('@/features/window-measuring-size'))
const DrawingDesigner = lazyWithRetry(() => import('@/features/glasspro-craft'))
const TemplatesComponent = lazyWithRetry(
  () => import('@/features/window-measuring-size/ui/TemplatesComponent'),
)
const ServicePriceList = lazyWithRetry(() => import('@/features/service-pricelist'))
const ServiceComponent = lazyWithRetry(
  () => import('@/features/window-measuring-size/ui/ServiceComponent'),
)
const NotificationsComponent = lazyWithRetry(() => import('@/features/notification-list'))
// -- Warehouse
const Warehouses = lazyWithRetry(() => import('@/features/warehouse-main'))
const InventoryInWarehouse = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/Inventory'),
)
const MaterialsInWarehouse = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/Materials'),
)
const RequestsInWarehouse = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/Requests'),
)
const AddMaterials = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/Materials/AddEdit'),
)
const AddReadyProducts = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/ReadyProducts/AddEdit'),
)
const ReadyProductsInWarehouse = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/ReadyProducts'),
)
const AddInventory = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/Inventory/AddEdit'),
)
const AddRequests = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/Requests/AddEdit'),
)
const LabelsInWarehouse = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/Labels'),
)
const LabelsSectionsInWarehouse = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/LabelsSections'),
)
const LabelsRowsInWarehouse = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/LabelsRows'),
)
const LabelsColumnsInWarehouse = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/LabelsColumns'),
)
const LabelsItemsInWarehouse = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/LabelsItems'),
)
const IndividualInventoryInWarehouse = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/Inventory/IndividualInventerization'),
)
const AddInventoryUnit = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/Inventory/AddInventoryInits'),
)
const NewIncomeById = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/Incomes/New/ListById/index'),
)
const ProblemsIncomeById = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/Incomes/Problems/ListById'),
)
const CompletedIncomeById = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/Incomes/Completed/ListById'),
)
const InProgressIncomeById = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/Incomes/InProgress/ListById'),
)
const IncomesLayout = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/Incomes'),
)
const MaterialOutcomes = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/MaterialOutcomes'),
)
const AddMaterialOutcomes = lazyWithRetry(
  () => import('@/features/warehouse-main/ui/SubComponents/MaterialOutcomes/AddEdit'),
)
// -- Supply
const RequestsInSupply = lazyWithRetry(() => import('@/features/supply-request-order'))
const AddRequestOrder = lazyWithRetry(() => import('@/features/supply-request-order/ui/AddEdit'))
const AllOrdersInSupply = lazyWithRetry(() => import('@/features/supply-all-orders'))
const EditAllRequestOrder = lazyWithRetry(() => import('@/features/supply-all-orders/ui/AddEdit'))
const RequestPermit = lazyWithRetry(() => import('@/features/supply-request-confirmed'))
const AddRequestPermit = lazyWithRetry(
  () => import('@/features/supply-request-confirmed/ui/AddEdit'),
)
const AddRequestPermit2 = lazyWithRetry(() => import('@/features/supply-request-permit/ui/AddEdit'))
const RequestOrderPage = lazyWithRetry(() => import('@/features/supply-request-order'))
const AddRequestNew = lazyWithRetry(() => import('@/features/supply-request-new/ui/AddEdit'))
const AddRequestInProcess = lazyWithRetry(
  () => import('@/features/supply-request-in-progress/ui/AddEdit'),
)
const AddRequestReceived = lazyWithRetry(
  () => import('@/features/supply-request-received/ui/AddEdit'),
)
const AddRequestDelayed = lazyWithRetry(
  () => import('@/features/supply-request-delayed/ui/AddEdit'),
)
const AddRequestCompleted = lazyWithRetry(
  () => import('@/features/supply-request-completed/ui/AddEdit'),
)
const AddRequestConfirmed = lazyWithRetry(
  () => import('@/features/supply-request-confirmed/ui/AddEdit'),
)
const RequestPermitAll = lazyWithRetry(() => import('@/features/supply-request-permit'))
const TransportationInSupply = lazyWithRetry(() => import('@/features/supply-transportation'))
// Named exports (hard rule #7: no default component exports) → map to { default }.
const TransportNakladnoyPage = lazyWithRetry(() =>
  import('@/features/transport-nakladnoy').then((m) => ({ default: m.TransportNakladnoyPage })),
)
const TtnDetailPage = lazyWithRetry(() =>
  import('@/features/transport-nakladnoy').then((m) => ({ default: m.TtnDetailPage })),
)
// -- Gate (Дарвоза) — named exports (hard rule #7) mapped to { default }.
const GateAwaitingPage = lazyWithRetry(() =>
  import('@/features/gate').then((m) => ({ default: m.GateAwaitingPage })),
)
const GateInspectingPage = lazyWithRetry(() =>
  import('@/features/gate').then((m) => ({ default: m.GateInspectingPage })),
)
const GateHistoryPage = lazyWithRetry(() =>
  import('@/features/gate').then((m) => ({ default: m.GateHistoryPage })),
)
const GateDetailPage = lazyWithRetry(() =>
  import('@/features/gate').then((m) => ({ default: m.GateDetailPage })),
)
// -- Accounting (Бухгалтерия) — named exports (hard rule #7) mapped to { default }.
const AccountingDashboardPage = lazyWithRetry(() =>
  import('@/features/accounting').then((m) => ({ default: m.DashboardPage })),
)
const AccountingLedgerPage = lazyWithRetry(() =>
  import('@/features/accounting').then((m) => ({ default: m.LedgerPage })),
)
const AccountingPendingPaymentsPage = lazyWithRetry(() =>
  import('@/features/accounting').then((m) => ({ default: m.PendingPaymentsPage })),
)
const AccountingInvoicesPage = lazyWithRetry(() =>
  import('@/features/accounting').then((m) => ({ default: m.InvoicesPage })),
)
const AccountingInvoiceDetailPage = lazyWithRetry(() =>
  import('@/features/accounting').then((m) => ({ default: m.InvoiceDetailPage })),
)
const AddTransportation = lazyWithRetry(() => import('@/features/supply-transportation/ui/AddEdit'))
const TransportationTabs = lazyWithRetry(
  () => import('@/features/supply-transportation/ui/SubComponents'),
)
// -- Approvement
const ApprovementSignature = lazyWithRetry(() => import('@/features/approvement-signature'))
const ApprovementPermits = lazyWithRetry(() => import('@/features/approvement-permits'))
const OrderSigningDetail = lazyWithRetry(
  () => import('@/features/approvement-signature/ui/OrderSigningDetail'),
)
const PermitSigningDetail = lazyWithRetry(
  () => import('@/features/approvement-permits/ui/PermitSigningDetail'),
)
const DistributionOfExpenses = lazyWithRetry(() => import('@/features/approvement-expenses'))
const DistributionOfExpensesById = lazyWithRetry(
  () => import('@/features/approvement-expenses/ui/list-by-id'),
)
// -- Finance
const Pricing = lazyWithRetry(() => import('@/features/finance-pricing'))
const MaterialPricing = lazyWithRetry(() => import('@/features/finance-material-pricing'))
const UnitPrice = lazyWithRetry(() => import('@/features/finance-unit-price'))
// -- HR
const UserNotifications = lazyWithRetry(() => import('@/features/hr-profile/ui/notifications'))
const ProfileSettings = lazyWithRetry(() => import('@/features/hr-profile/ui/profile-settings'))
// -- Sign Groups
const SignGroups = lazyWithRetry(() => import('@/features/sign-groups'))
const SignGroupModify = lazyWithRetry(() => import('@/features/sign-groups/ui/modify'))
// -- Sales
const OrdersPage = lazyWithRetry(() => import('@/features/sales-orders'))
const CreateOrder = lazyWithRetry(() => import('@/features/sales-orders/ui/AddOrder'))
const TemplateConstructorComponent = lazyWithRetry(
  () => import('@/features/sales-orders/ui/AddOrder/TemplateConstructorComponent'),
)
// -- Production
const Planning = lazyWithRetry(() => import('@/features/production-planning'))
const WorkFlowControl = lazyWithRetry(() => import('@/features/production-workflow-control'))
const WorkStations = lazyWithRetry(() => import('@/features/production-work-stations'))
const OrderFlowControl = lazyWithRetry(() => import('@/features/production-order-flow-control'))
// -- User
const SignPermission = lazyWithRetry(() => import('@/features/sign-permission'))
// -- Other
const UnderDevelopment = lazyWithRetry(() => import('@/features/under-development'))

export const useTranslationData = (): import('@/shared/config/route-config').RouteConfig[] => {
  const { t } = useTranslation()
  return [
    {
      id: 1,
      title: t('home'),
      pathname: '/',
      name: 'HOME',
      search: '?',
      component: Home,
      hidden: false,
      child: [],
    },
    {
      id: 2,
      title: t('data-library'),
      pathname: 'data-library',
      name: 'DATA_LIBRARY',
      search: '?',
      hidden: false,
      mustRedirect: true,
      child: [
        {
          id: 2100,
          parentId: 21,
          title: t('/categories'),
          group: 'BASIC_REFERENCES',
          pathname: 'data-library/categories',
          name: 'CATEGORY',
          component: Category,
          hidden: false,
          child: [],
        },
        {
          id: 2101,
          parentId: 21,
          title: t('/category-order'),
          group: 'BASIC_REFERENCES',
          name: 'CATEGORY_ORDER',
          pathname: 'data-library/category-order',
          component: CategoryOrderComponent,
          hidden: false,
          child: [],
        },
        {
          id: 2102,
          parentId: 21,
          title: t('/measurement'),
          group: 'BASIC_REFERENCES',
          name: 'MEASURE',
          pathname: 'data-library/measurement',
          component: Measurement,
          hidden: false,
          child: [],
        },
        {
          id: 2103,
          parentId: 21,
          title: t('/height-length'),
          group: 'BASIC_REFERENCES',
          name: 'HEIGHT_LENGTH',
          pathname: 'data-library/height-length',
          component: LenghtHeightComponent,
          hidden: false,
          child: [],
        },
        {
          id: 2104,
          parentId: 21,
          title: t('/glass-thickness'),
          group: 'BASIC_REFERENCES',
          name: 'WIDTH',
          pathname: 'data-library/glass-thickness',
          component: GlassThickness,
          hidden: false,
          child: [],
        },
        {
          id: 2105,
          parentId: 21,
          title: t('/materials'),
          group: 'BASIC_REFERENCES',
          name: 'MATERIAL',
          pathname: 'data-library/materials',
          component: Materials,
          hidden: false,
          child: [],
        },
        {
          id: 2109,
          parentId: 21,
          title: t('/services'),
          group: 'BASIC_REFERENCES',
          name: 'READY_PRODUCT',
          pathname: 'data-library/services',
          component: ServiceComponent,
          hidden: false,
          child: [],
        },
        {
          id: 2106,
          parentId: 21,
          title: t('/templates'),
          group: 'BASIC_REFERENCES',
          name: 'READY_PRODUCT',
          pathname: 'data-library/templates',
          component: TemplatesComponent,
          hidden: false,
          child: [],
        },
        {
          id: 2107,
          parentId: 21,
          title: t('create_template'),
          group: 'BASIC_REFERENCES',
          name: 'READY_PRODUCT',
          pathname: 'data-library/templates/:model_id/*',
          component: WindowDesigner,
          hidden: false,
          child: [],
        },
        // {
        //   id: 21071,
        //   parentId: 21,
        //   title: t('create_template'),
        //   group: 'BASIC_REFERENCES',
        //   name: 'READY_PRODUCT',
        //   pathname: 'data-library/templates/:model_id/materials',
        //   component: MaterialsComponent,
        //   hidden: false,
        //   child: [],
        // },
        // {
        //   id: 21071,
        //   parentId: 21,
        //   title: t('create_template'),
        //   group: 'BASIC_REFERENCES',
        //   name: 'READY_PRODUCT',
        //   pathname: 'data-library/templates/:model_id/services',
        //   component: ServicesComponent,
        //   hidden: false,
        //   child: [],
        // },
        {
          id: 2108,
          parentId: 21,
          title: t('/ready-products'),
          group: 'BASIC_REFERENCES',
          name: 'READY_PRODUCT',
          pathname: 'data-library/ready-products',
          component: ReadyProductsComponent,
          hidden: false,
          child: [],
        },
        {
          id: 2106,
          parentId: 21,
          title: t('/equipments'),
          group: 'BASIC_REFERENCES',
          name: 'INVENTORY',
          pathname: 'data-library/equipments',
          component: EquipmentsComponent,
          hidden: false,
          child: [],
        },
        {
          id: 2107,
          parentId: 21,
          title: t('/doc-types'),
          group: 'BASIC_REFERENCES',
          name: 'DOC_TYPE',
          pathname: 'data-library/doc-types',
          component: DoctypesComponent,
          hidden: false,
          child: [],
        },

        {
          id: 2200,
          parentId: 22,
          title: t('/company'),
          group: 'ORGANIZATIONAL_STRUCTURE',
          name: 'COMPANY',
          pathname: 'data-library/company',
          component: Company,
          hidden: false,
          child: [],
        },

        {
          id: 2300,
          parentId: 23,
          title: t('/customers'),
          group: 'CONTRACT_PARTNERS',
          name: 'CUSTOMER',
          pathname: 'data-library/customers',
          component: Customers,
          hidden: false,
          child: [],
        },

        {
          id: 2400,
          parentId: 24,
          title: t('/employees'),
          group: 'USER_MANAGEMENT',
          name: 'EMPLOYEE',
          pathname: 'data-library/employees',
          component: Employees,
          hidden: false,
          child: [],
        },
        {
          id: 2401,
          parentId: 24,
          title: t('/signatories'),
          group: 'USER_MANAGEMENT',
          name: 'EMPLOYEE',
          pathname: 'data-library/signatories',
          component: Signatories,
          hidden: false,
          child: [],
        },
        {
          id: 2402,
          parentId: 24,
          title: t('/roles'),
          group: 'USER_MANAGEMENT',
          name: 'ROLE',
          pathname: 'data-library/roles',
          component: Roles,
          hidden: false,
          child: [],
        },
        {
          id: 2403,
          parentId: 24,
          title: t('/positions'),
          group: 'USER_MANAGEMENT',
          name: 'POSITION',
          pathname: 'data-library/positions',
          component: Positions,
          hidden: false,
          child: [],
        },

        {
          id: 2500,
          parentId: 25,
          title: t('/districts'),
          group: 'OPERATIONAL_DATA',
          name: 'ADDRESS',
          pathname: 'data-library/districts',
          component: Districts,
          hidden: false,
          child: [],
        },
        {
          id: 2501,
          parentId: 25,
          title: t('/departments'),
          group: 'OPERATIONAL_DATA',
          name: 'DEPARTMENT',
          pathname: 'data-library/departments',
          component: Departments,
          hidden: false,
          child: [],
        },
        {
          id: 2501,
          parentId: 25,
          title: t('/min-max-percentage'),
          group: 'OPERATIONAL_DATA',
          name: 'DEPARTMENT',
          pathname: 'data-library/min-max-percentage',
          component: MinMaxPercentage,
          hidden: false,
          child: [],
        },
        {
          id: 2502,
          parentId: 25,
          title: t('/lables'),
          group: 'OPERATIONAL_DATA',
          name: 'LABEL',
          pathname: 'data-library/lables',
          component: Lables,
          hidden: false,
          child: [],
        },
        {
          id: 2503,
          parentId: 25,
          title: t('/regions'),
          group: 'OPERATIONAL_DATA',
          name: 'ADDRESS',
          pathname: 'data-library/regions',
          component: Regions,
          hidden: false,
          child: [],
        },
        {
          id: 2504,
          parentId: 25,
          title: t('/branches'),
          group: 'OPERATIONAL_DATA',
          name: 'COMPANY',
          pathname: 'data-library/branches',
          component: Branches,
          hidden: false,
          child: [],
        },
        {
          id: 2505,
          parentId: 25,
          title: t('/warehouse'),
          group: 'OPERATIONAL_DATA',
          name: 'WAREHOUSE',
          pathname: 'data-library/warehouse',
          component: Warehouse,
          hidden: false,
          child: [],
        },
        {
          id: 2506,
          parentId: 25,
          title: t('/service-price-list'),
          group: 'OPERATIONAL_DATA',
          name: 'SERVICE_PRICE_LIST',
          pathname: 'data-library/service-price-list',
          component: ServicePriceList,
          hidden: false,
          child: [],
        },
        {
          id: 2507,
          parentId: 25,
          title: t('/transport'),
          group: 'OPERATIONAL_DATA',
          name: 'TRANSPORT',
          pathname: 'data-library/transport',
          component: TransportsComponent,
          hidden: false,
          child: [],
        },
        {
          id: 2508,
          parentId: 25,
          title: t('/notifications_settings'),
          group: 'OPERATIONAL_DATA',
          name: 'NOTIFICATION_SETTING',
          pathname: 'data-library/notifications-settings',
          component: NotificationsComponent,
          hidden: false,
          child: [],
        },
      ],
    },
    {
      id: 3,
      title: t('warehouse'),
      pathname: 'warehouse',
      search: '?',
      name: 'WAREHOUSE',
      hidden: false,
      child: [
        {
          id: 31,
          parentId: 3,
          title: t('stocks-management'),
          pathname: 'warehouse/stocks-management',
          search: '?',
          name: 'WAREHOUSE',
          component: Warehouses,
          hidden: false,
          child: [
            {
              id: 3100,
              parentId: 31,
              title: t('materials'),
              pathname: 'warehouse/stocks-management/:id/materials',
              name: 'MATERIAL',
              component: MaterialsInWarehouse,
              hidden: false,
              child: [],
            },
            {
              id: 3101,
              parentId: 31,
              title: t('menu_unit_for_material'),
              pathname: 'warehouse/stocks-management/:id/materials/create-request',
              name: 'UNIT_INCOME',
              component: AddMaterials,
              hidden: false,
              child: [],
            },
            {
              id: 3102,
              parentId: 31,
              title: t('ready-products'),
              pathname: 'warehouse/stocks-management/:id/ready-products',
              name: 'READY_PRODUCT',
              component: UnderDevelopment,
              hidden: false,
              child: [],
            },
            {
              id: 3103,
              parentId: 31,
              title: t('ready-products'),
              name: 'READY_PRODUCT',
              pathname: 'warehouse/stocks-management/:id/ready-products/create-request',
              component: AddReadyProducts,
              hidden: false,
              child: [],
            },
            {
              id: 3104,
              parentId: 31,
              title: t('inventory'),
              name: 'INVENTORY',
              pathname: 'warehouse/stocks-management/:id/inventory',
              component: UnderDevelopment,
              hidden: false,
              child: [],
            },
            {
              id: 3105,
              parentId: 31,
              title: t('inventory'),
              name: 'INVENTORY_PROCESS',
              pathname: 'warehouse/stocks-management/:id/inventorization',
              component: InventoryInWarehouse,
              hidden: false,
              child: [],
            },
            {
              id: 3106,
              parentId: 31,
              title: t('inventory'),
              pathname: 'warehouse/stocks-management/:id/inventorization/:inventarizationNumber',
              name: 'INVENTORY_PROCESS',
              component: IndividualInventoryInWarehouse,
              hidden: false,
              child: [],
            },
            {
              id: 3107,
              parentId: 31,
              title: t('inventory'),
              name: 'INVENTORY_PROCESS',
              pathname:
                'warehouse/stocks-management/:id/inventorization/:inventarizationNumber/add-inventory',
              component: AddInventoryUnit,
              hidden: false,
              child: [],
            },
            {
              id: 3108,
              parentId: 31,
              title: t('inventory'),
              pathname: 'warehouse/stocks-management/:id/inventorization/new-inventorization',
              name: 'INVENTORY_PROCESS',
              component: AddInventory,
              hidden: false,
              child: [],
            },
            {
              id: 3109,
              parentId: 31,
              title: t('inventory'),
              pathname: 'warehouse/stocks-management/:id/inventorization/create-request',
              name: 'INVENTORY_PROCESS',
              component: AddInventory,
              hidden: false,
              child: [],
            },
            {
              id: 3110,
              parentId: 31,
              title: t('requests'),
              pathname: 'warehouse/stocks-management/:id/requests',
              name: 'REQUEST',
              component: RequestsInWarehouse,
              hidden: false,
              child: [],
            },
            {
              id: 3111,
              parentId: 31,
              title: t('requests'),
              name: 'REQUEST_ORDER',
              pathname: 'warehouse/stocks-management/:id/requests/create-request',
              component: AddRequests,
              hidden: false,
              child: [],
            },
            {
              id: 3112,
              parentId: 31,
              title: t('placement_of_material'),
              pathname: 'warehouse/stocks-management/:id/placement-of-material',
              name: 'LABEL',
              component: LabelsInWarehouse,
              hidden: false,
              child: [],
            },
            {
              id: 311000,
              parentId: 3110,
              title: t('sections'),
              name: 'LABEL',
              pathname: 'warehouse/stocks-management/:id/placement-of-material/sections/:sectionId',
              component: LabelsSectionsInWarehouse,
              hidden: false,
              child: [],
            },
            {
              id: 31100000,
              parentId: 311000,
              title: t('rows'),
              name: 'LABEL',

              pathname:
                'warehouse/stocks-management/:id/placement-of-material/sections/:sectionId/rows/:rowId',
              component: LabelsRowsInWarehouse,
              hidden: false,
              child: [],
            },
            {
              id: 3110000000,
              parentId: 31100000,
              title: t('columns'),
              pathname:
                'warehouse/stocks-management/:id/placement-of-material/sections/:sectionId/rows/:rowId/columns/:columnId',
              name: 'LABEL',

              component: LabelsColumnsInWarehouse,
              hidden: false,
              child: [],
            },
            {
              id: 311000000000,
              parentId: 3110000000,
              title: t('items'),
              name: 'LABEL',

              pathname:
                'warehouse/stocks-management/:id/placement-of-material/sections/:sectionId/rows/:rowId/columns/:columnId/items/:itemId',
              component: LabelsItemsInWarehouse,
              hidden: false,
              child: [],
            },
            {
              id: 3111,
              parentId: 31,
              title: t('responsible_person'),
              pathname: 'warehouse/stocks-management/:id/responsible-person',
              name: 'REQUEST',
              component: UnderDevelopment,
              hidden: false,
              child: [],
            },
            {
              id: 3112,
              parentId: 31,
              title: t('responsible_person'),
              pathname: 'warehouse/stocks-management/:id/responsible-person',
              name: 'REQUEST',
              component: UnderDevelopment,
              hidden: false,
              child: [],
            },

            {
              id: 3113,
              parentId: 31,
              title: t('incomes'),
              pathname: 'warehouse/stocks-management/:id/incomes',
              name: 'UNIT_INCOME',
              hidden: false,
              component: IncomesLayout,
              // mustRedirect: true,
              child: [
                {
                  id: 311300,
                  parentId: 3113,
                  title: t('new-income'),
                  pathname: 'warehouse/stocks-management/:id/incomes/new-income',
                  name: 'UNIT_INCOME',
                  component: IncomesLayout,
                  hidden: false,
                  child: [],
                },
                {
                  id: 311301,
                  parentId: 3113,
                  title: t('create-traportation-income'),
                  pathname:
                    'warehouse/stocks-management/:id/incomes/new-income/:transportationNumber',
                  name: 'UNIT_INCOME',
                  component: NewIncomeById,
                  hidden: false,
                  child: [],
                },
                {
                  id: 311302,
                  parentId: 3113,
                  title: t('in-progress-income'),
                  pathname: 'warehouse/stocks-management/:id/incomes/in-progress-income',
                  name: 'UNIT_INCOME',
                  component: IncomesLayout,
                  hidden: false,
                  child: [],
                },
                {
                  id: 311303,
                  parentId: 3113,
                  title: t('create-traportation-income'),
                  pathname:
                    'warehouse/stocks-management/:id/incomes/in-progress-income/:transportationNumber',
                  name: 'UNIT_INCOME',
                  component: InProgressIncomeById,
                  hidden: false,
                  child: [],
                },
                {
                  id: 311304,
                  parentId: 3113,
                  title: t('completed-income'),
                  pathname: 'warehouse/stocks-management/:id/incomes/completed-income',
                  name: 'UNIT_INCOME',
                  component: IncomesLayout,
                  hidden: false,
                  child: [],
                },
                {
                  id: 311305,
                  parentId: 3113,
                  title: t('create-traportation-income'),
                  pathname:
                    'warehouse/stocks-management/:id/incomes/completed-income/:transportationNumber',
                  name: 'UNIT_INCOME',
                  component: CompletedIncomeById,
                  hidden: false,
                  child: [],
                },
                {
                  id: 311306,
                  parentId: 3113,
                  title: t('problems-income'),
                  pathname: 'warehouse/stocks-management/:id/incomes/problems-income',
                  name: 'UNIT_INCOME',
                  component: IncomesLayout,
                  hidden: false,
                  child: [],
                },
                {
                  id: 311307,
                  parentId: 3113,
                  title: t('create-traportation-income'),
                  pathname:
                    'warehouse/stocks-management/:id/incomes/problems-income/:transportationNumber',
                  name: 'UNIT_INCOME',
                  component: ProblemsIncomeById,
                  hidden: false,
                  child: [],
                },
              ],
            },

            {
              id: 3114,
              parentId: 31,
              title: t('material-outcomes'),
              pathname: 'warehouse/stocks-management/:id/material-outcomes',
              name: 'REQUEST',
              component: MaterialOutcomes,
              hidden: false,
              child: [],
            },
            {
              id: 311400,
              parentId: 3114,
              title: t('create-outcomes'),
              name: 'REQUEST_ORDER',
              pathname: 'warehouse/stocks-management/:id/material-outcomes/create-outcomes',
              component: AddMaterialOutcomes,
              hidden: false,
              child: [],
            },
            {
              id: 3115,
              parentId: 31,
              title: t('reports'),
              pathname: 'warehouse/stocks-management/:id/report',
              name: 'REQUEST',
              component: UnderDevelopment,
              hidden: false,
              child: [],
            },
            {
              id: 3116,
              parentId: 31,
              title: t('warehouse_settings_title'),
              pathname: 'warehouse/stocks-management/:id/settings',
              name: 'WAREHOUSE',
              component: UnderDevelopment,
              hidden: false,
              child: [],
            },
          ],
        },
      ],
    },

    {
      id: 4,
      title: t('supply'),
      pathname: 'supply',
      search: '?',
      name: 'SUPPLY',
      hidden: false,
      child: [
        {
          id: 41,
          parentId: 4,
          title: t('request-order'),
          pathname: 'supply/request-order',
          name: 'REQUEST_ORDER',
          hidden: false,
          mustRedirect: true,
          child: [
            {
              id: 4101,
              parentId: 41,
              title: t('request-in-process'),
              pathname: 'supply/request-order/new',
              name: 'REQUEST_ORDER',
              component: RequestOrderPage,
              hidden: false,
              child: [],
            },
            {
              id: 4102,
              parentId: 41,
              title: t('create-request-order'),
              pathname: 'supply/request-order/new/create-request-new',
              name: 'REQUEST_ORDER',
              component: AddRequestNew,
              hidden: false,
              child: [],
            },
            {
              id: 4103,
              parentId: 41,
              title: t('request-in-process'),
              pathname: 'supply/request-order/confirmed',
              name: 'REQUEST_ORDER',
              component: RequestOrderPage,
              hidden: false,
              child: [],
            },
            {
              id: 4104,
              parentId: 41,
              title: t('create-request-permit'),
              pathname: 'supply/request-order/confirmed/create-request-permit',
              name: 'REQUEST_ORDER',
              component: AddRequestConfirmed,
              hidden: false,
              child: [],
            },
            {
              id: 4105,
              parentId: 41,
              title: t('request-in-process'),
              pathname: 'supply/request-order/in-progress',
              name: 'REQUEST_ORDER',
              component: RequestOrderPage,
              hidden: false,
              child: [],
            },
            {
              id: 4106,
              parentId: 41,
              title: t('create-request-order'),
              pathname: 'supply/request-order/in-progress/create-request-in-progress',
              name: 'REQUEST_ORDER',
              component: AddRequestInProcess,
              hidden: false,
              child: [],
            },
            {
              id: 4107,
              parentId: 41,
              title: t('request-in-process'),
              pathname: 'supply/request-order/received',
              name: 'REQUEST_ORDER',
              component: RequestOrderPage,
              hidden: false,
              child: [],
            },
            {
              id: 4108,
              parentId: 41,
              title: t('create-request-order'),
              pathname: 'supply/request-order/received/create-request-received',
              name: 'REQUEST_ORDER',
              component: AddRequestReceived,
              hidden: false,
              child: [],
            },
            {
              id: 4109,
              parentId: 41,
              title: t('request-in-process'),
              pathname: 'supply/request-order/completed',
              name: 'REQUEST_ORDER',
              component: RequestOrderPage,
              hidden: false,
              child: [],
            },
            {
              id: 4110,
              parentId: 41,
              title: t('create-request-order'),
              pathname: 'supply/request-order/completed/create-request-completed',
              name: 'REQUEST_ORDER',
              component: AddRequestCompleted,
              hidden: false,
              child: [],
            },
            {
              id: 4111,
              parentId: 41,
              title: t('request-in-process'),
              pathname: 'supply/request-order/history',
              name: 'REQUEST_ORDER',
              component: RequestOrderPage,
              hidden: false,
              child: [],
            },
            // {
            //   id: 4112,
            //   parentId: 41,
            //   title: t('create-request-order'),
            //   pathname: 'supply/request-order/history/create-request-history',
            //   name: 'REQUEST_ORDER',
            //   component: AddRequestDelayed,
            //   hidden: false,
            //   child: [],
            // },
          ],
        },

        {
          id: 42,
          parentId: 4,
          title: t('all-orders'),
          pathname: 'supply/all-orders',
          name: 'REQUEST_ORDER',
          component: AllOrdersInSupply,
          hidden: false,
          child: [
            {
              id: 421,
              parentId: 42,
              title: '',
              pathname: 'supply/all-orders/:orderNumber',
              name: 'REQUEST_ORDER',
              component: OrderSigningDetail,
              hidden: true,
              child: [],
            },
          ],
        },
        {
          id: 4100,
          parentId: 41,
          title: t('edit-request-order'),
          pathname: 'supply/all-orders/edit-request-order',
          name: 'REQUEST_ORDER',
          component: EditAllRequestOrder,
          hidden: false,
          child: [],
        },
        {
          id: 43,
          parentId: 4,
          title: t('all-request-permit'),
          pathname: 'supply/all-request-permit',
          name: 'REQUEST_PERMIT',
          component: RequestPermitAll,
          hidden: false,
          child: [
            {
              id: 431,
              parentId: 43,
              title: '',
              pathname: 'supply/all-request-permit/:permitNumber',
              name: 'REQUEST_PERMIT',
              component: PermitSigningDetail,
              hidden: true,
              child: [],
            },
          ],
        },
        {
          id: 4100,
          parentId: 41,
          title: t('create-request-permit'),
          pathname: 'supply/all-request-permit/create-request-permit',
          name: 'REQUEST_PERMIT',
          component: AddRequestPermit2,
          hidden: false,
          child: [],
        },
        {
          // Table view — the existing transportation list, now at /list.
          // The visible Supply menu item lands here; a header toggle swaps
          // to /kanban. Bare /supply/transport-invoice redirects here (AppRoutes).
          id: 44,
          parentId: 4,
          title: t('transport-invoice'),
          pathname: 'supply/transport-invoice/list',
          name: 'TRANSPORTATION',
          mustRedirect: true,
          component: TransportationInSupply,
          hidden: false,
          child: [],
        },
        {
          id: 46,
          parentId: 4,
          title: t('materials'),
          pathname: 'supply/transport-invoice/:transportationNumber',
          name: 'TRANSPORTATION',
          component: TransportationTabs,
          hidden: false,
          child: [],
        },
        {
          id: 45,
          parentId: 4,
          title: t('create-request-permit'),
          pathname: 'supply/transport-invoice/create-request-permit',
          name: 'TRANSPORTATION',
          component: AddTransportation,
          hidden: false,
          child: [],
        },
        {
          // Kanban view — same Supply leaf as the table, reached via the
          // header toggle on /list. `hidden: false` matches sibling id 44
          // (table view): both are views of the same resource, so they
          // share access policy. No separate sidebar menu item is produced
          // because the Supply menu is hand-curated in MenuItems.tsx
          // (does not auto-derive from SidebarData children). Same precedent
          // as the Gate routes below. `hidden: true` here would let the
          // AppRoutes filter `hidden && !permission[name]?.VIEW` silently
          // drop this route, after which the sibling `:transportationNumber`
          // route would swallow `/kanban` as a param and bounce to /list.
          // Static `kanban` segment outranks `:transportationNumber`.
          id: 447,
          parentId: 4,
          title: t('transport-nakladnoy:page_title'),
          pathname: 'supply/transport-invoice/kanban',
          name: 'TRANSPORTATION',
          component: TransportNakladnoyPage,
          hidden: false,
          child: [],
        },
        {
          // TTN detail — nested under /kanban so its param can't collide with
          // the sibling `:transportationNumber` transportation-detail route.
          // Uses numeric id (matches GET /v1/transport-nakladnoy/{id}/by-id);
          // see TRANSPORT_NAKLADNOY_GAPS.md #1 (RESOLVED). Same `hidden: false`
          // rationale as id 447.
          id: 448,
          parentId: 4,
          title: t('transport-nakladnoy:page_title'),
          pathname: 'supply/transport-invoice/kanban/:id',
          name: 'TRANSPORTATION',
          component: TtnDetailPage,
          hidden: false,
          child: [],
        },
      ],
    },
    {
      // Дарвоза (Gate) — top-level group. name 'GATE' is unique so AppRoutes
      // resolves it via routes.find(r => r.name === 'GATE'). The sidebar menu
      // is defined separately in useGroupedMenuGate (MenuItems.tsx).
      // hidden:false (ungated) is INTENTIONAL, not an oversight: no backend
      // `permission.GATE` exists yet (GATE_GAPS.md #9). Gating must flip together
      // across the Home shortcut + sidebar menu + these routes — gating the routes
      // alone 404s a still-visible menu entry. Re-gate all three when GATE ships.
      id: 29,
      title: t('gate'),
      pathname: 'gate',
      search: '?',
      name: 'GATE',
      hidden: false,
      child: [
        {
          id: 291,
          parentId: 29,
          title: t('gate:view.awaiting'),
          pathname: 'gate/awaiting',
          name: 'GATE',
          component: GateAwaitingPage,
          hidden: false,
          child: [],
        },
        {
          id: 292,
          parentId: 29,
          title: t('gate:view.inspecting'),
          pathname: 'gate/inspecting',
          name: 'GATE',
          component: GateInspectingPage,
          hidden: false,
          child: [],
        },
        {
          id: 293,
          parentId: 29,
          title: t('gate:view.history'),
          pathname: 'gate/history',
          name: 'GATE',
          component: GateHistoryPage,
          hidden: false,
          child: [],
        },
        {
          // Detail — static `pass` segment + :gatePassNumber. hidden: not a menu
          // item; rendered as a route (hidden:false avoids permission gating).
          id: 294,
          parentId: 29,
          title: t('gate:page_title'),
          pathname: 'gate/pass/:gatePassNumber',
          name: 'GATE',
          component: GateDetailPage,
          hidden: false,
          child: [],
        },
      ],
    },

    {
      // ACCOUNTING (Бухгалтерия) — Phase 0 foundation. AppRoutes consumes the
      // children via routes.find(r => r.name === 'ACCOUNTING'). The sidebar menu
      // entry (MenuItems.tsx → useGroupedMenuAccounting) is NOT wired yet — the
      // 4 routes are URL-reachable but invisible in the side menu. That's the
      // intended Phase-0 smoke-test surface; sidebar wiring lands with Phase 1.
      // hidden:false (ungated) is INTENTIONAL — no backend `permission.ACCOUNTING`
      // exists yet (see ACCOUNTING_GAPS.md A1). Re-gate when the backend ships
      // the permission row; gating the routes alone 404s any future menu entry.
      id: 30,
      title: t('accounting:nav.title'),
      pathname: 'accounting',
      search: '?',
      name: 'ACCOUNTING',
      hidden: false,
      mustRedirect: true,
      child: [
        {
          id: 301,
          parentId: 30,
          title: t('accounting:nav.dashboard'),
          pathname: 'accounting/dashboard',
          name: 'ACCOUNTING',
          component: AccountingDashboardPage,
          hidden: false,
          child: [],
        },
        {
          id: 302,
          parentId: 30,
          title: t('accounting:nav.transactions'),
          pathname: 'accounting/transactions',
          name: 'ACCOUNTING',
          component: AccountingLedgerPage,
          hidden: false,
          child: [],
        },
        {
          id: 303,
          parentId: 30,
          title: t('accounting:nav.pending_payments'),
          pathname: 'accounting/pending-payments',
          name: 'ACCOUNTING',
          component: AccountingPendingPaymentsPage,
          hidden: false,
          child: [],
        },
        {
          id: 304,
          parentId: 30,
          title: t('accounting:nav.invoices'),
          pathname: 'accounting/invoices',
          name: 'ACCOUNTING',
          component: AccountingInvoicesPage,
          hidden: false,
          child: [],
        },
        {
          // Detail entry — not a sidebar menu item (the menu list lives in
          // `useGroupedMenuAccounting` in `pages/Warehouse/MainComponents/
          // MenuItems.tsx`). `hidden: false` here means "register the route
          // unconditionally for any authenticated user"; matches the
          // convention sales orders detail entries (ids 87, 88) follow.
          // Using `hidden: true` would gate it behind
          // `permission.ACCOUNTING?.VIEW` (AppRoutes.tsx:349), which is
          // undefined for users whose permission map doesn't enumerate
          // ACCOUNTING explicitly — and the route would 404 even though
          // the list page is visible. The same SidebarData → AppRoutes
          // wiring drives every accounting page.
          id: 305,
          parentId: 30,
          title: t('accounting:nav.invoices'),
          pathname: 'accounting/invoices/:invoiceId',
          name: 'ACCOUNTING',
          component: AccountingInvoiceDetailPage,
          hidden: false,
          child: [],
        },
      ],
    },

    {
      id: 5,
      title: t('incoming-documents'),
      pathname: 'incoming-documents',
      search: '?',
      name: 'REQUEST_ORDER_SIGNATURE',
      hidden: false,
      mustRedirect: true,
      child: [
        {
          id: 51,
          parentId: 5,
          title: t('orders'),
          pathname: 'incoming-documents/orders',
          name: 'REQUEST_ORDER_SIGNATURE',
          component: ApprovementSignature,
          hidden: false,
          child: [
            {
              id: 511,
              parentId: 51,
              title: '',
              pathname: 'incoming-documents/orders/:orderNumber',
              name: 'REQUEST_ORDER_SIGNATURE',
              component: OrderSigningDetail,
              hidden: true,
              child: [],
            },
          ],
        },
        {
          id: 52,
          parentId: 5,
          title: t('permits'),
          pathname: 'incoming-documents/permits',
          name: 'REQUEST_PERMIT_SIGNATURE',
          component: ApprovementPermits,
          hidden: false,
          child: [
            {
              id: 521,
              parentId: 52,
              title: '',
              pathname: 'incoming-documents/permits/:permitNumber',
              name: 'REQUEST_PERMIT_SIGNATURE',
              component: PermitSigningDetail,
              hidden: true,
              child: [],
            },
          ],
        },
      ],
    },
    {
      id: 6,
      title: t('finance'),
      pathname: 'finance',
      search: '?',
      name: 'TRANSPORTATION',
      hidden: false,
      child: [
        {
          id: 61,
          parentId: 6,
          title: t('distribution-of-expenses'),
          pathname: 'finance/distribution-of-expenses',
          name: 'TRANSPORTATION',
          component: DistributionOfExpenses,
          hidden: false,
          child: [],
        },
        {
          id: 62,
          parentId: 6,
          title: t('distribution-of-expenses'),
          pathname: 'finance/distribution-of-expenses/:id',
          name: 'TRANSPORTATION',
          component: DistributionOfExpensesById,
          hidden: false,
          child: [],
        },
        {
          id: 63,
          parentId: 6,
          title: t('pricing'),
          pathname: 'finance/pricing',
          name: 'TRANSPORTATION',
          component: MaterialPricing,
          hidden: false,
          child: [],
        },
        {
          id: 64,
          parentId: 6,
          title: t('pricing'),
          pathname: 'finance/pricing/:id',
          name: 'TRANSPORTATION',
          component: Pricing,
          hidden: false,
          child: [],
        },
        {
          id: 65,
          parentId: 6,
          title: t('unit-price'),
          pathname: 'finance/unit-price',
          name: 'TRANSPORTATION',
          component: UnitPrice,
          hidden: false,
          child: [],
        },
      ],
    },
    {
      id: 7,
      title: t('user'),
      pathname: 'user',
      search: '?',
      name: 'USER',
      hidden: false,
      child: [
        {
          id: 71,
          parentId: 7,
          title: t('user-profile'),
          pathname: 'user/user-profile',
          name: 'USER',
          component: SignPermission,
          hidden: false,
          child: [],
        },
        {
          id: 72,
          parentId: 7,
          title: t('notifications'),
          pathname: 'user/notifications',
          name: 'USER',
          component: UserNotifications,
          hidden: false,
          child: [],
        },
        {
          id: 73,
          parentId: 7,
          title: t('profile-settings'),
          pathname: 'user/profile-settings',
          name: 'USER',
          component: ProfileSettings,
          hidden: false,
          child: [],
        },
        {
          id: 74,
          parentId: 7,
          title: t('logout'),
          pathname: '/logout',
          name: 'USER',
          hidden: false,
          child: [],
        },
      ],
    },
    {
      id: 8,
      title: t('sales'),
      pathname: 'sales',
      search: '?',
      name: 'SALES',
      hidden: false,
      child: [
        {
          id: 81,
          parentId: 8,
          title: t('/templates'),
          name: 'SALES',
          pathname: 'sales/templates',
          component: TemplatesComponent,
          hidden: false,
          child: [],
        },
        {
          id: 82,
          parentId: 8,
          title: t('/services'),
          name: 'SALES',
          pathname: 'sales/services',
          component: ServiceComponent,
          hidden: false,
          child: [],
        },
        {
          id: 83,
          parentId: 8,
          title: t('/service-price-list'),
          name: 'SALES',
          pathname: 'sales/service-price-list',
          component: ServicePriceList,
          hidden: false,
          child: [],
        },
        {
          id: 842,
          parentId: 8,
          title: t('drawing'),
          name: 'SALES',
          pathname: 'sales/templates/create-template',
          component: DrawingDesigner,
          hidden: false,
          child: [],
        },
        {
          id: 84,
          parentId: 8,
          title: t('create_template'),
          name: 'SALES',
          pathname: 'sales/templates/*',
          component: WindowDesigner,
          hidden: false,
          child: [],
        },
        {
          id: 84,
          parentId: 8,
          title: t('materials&services'),
          name: 'SALES',
          pathname: 'sales/templates/materials-and-services',
          component: WindowDesigner,
          hidden: false,
          child: [],
        },
        {
          id: 841,
          parentId: 8,
          title: t('files'),
          name: 'SALES',
          pathname: 'sales/templates/files',
          component: WindowDesigner,
          hidden: false,
          child: [],
        },
        {
          id: 85,
          parentId: 8,
          title: t('create_template'),
          name: 'SALES',
          pathname: 'sales/templates/:model_id/*',
          component: WindowDesigner,
          hidden: false,
          child: [],
        },
        {
          id: 86,
          parentId: 8,
          title: t('sales'),
          pathname: 'sales/orders/*',
          name: 'SALES',
          component: OrdersPage,
          hidden: false,
          child: [],
        },
        // {
        //   id: 87,
        //   parentId: 8,
        //   title: t('create_order'),
        //   pathname: 'sales/orders/edit/:orderId',
        //   name: 'SALES',
        //   component: CreateOrder,
        //   hidden: false,
        //   child: [],
        // },
        {
          id: 87,
          parentId: 8,
          title: t('create_order'),
          pathname: 'sales/orders/edit/:orderId/:orderType',
          name: 'SALES',
          component: CreateOrder,
          hidden: false,
          child: [],
        },
        {
          id: 88,
          parentId: 8,
          title: t('sales'),
          pathname: 'sales/orders/order/:orderId/:tab/:modelId?',
          name: 'SALES',
          component: TemplateConstructorComponent,
          hidden: false,
          child: [],
        },
      ],
    },
    {
      id: 9,
      title: t('hr'),
      pathname: 'hr',
      search: '?',
      name: 'HR',
      hidden: false,
      child: [
        {
          id: 91,
          parentId: 9,
          title: t('/roles'),
          name: 'HR',
          pathname: 'hr/roles',
          component: Roles,
          hidden: false,
          child: [],
        },
        {
          id: 92,
          parentId: 9,
          title: t('/positions'),
          name: 'HR',
          pathname: 'hr/positions',
          component: Positions,
          hidden: false,
          child: [],
        },
        {
          id: 93,
          parentId: 9,
          title: t('/employees'),
          name: 'HR',
          pathname: 'hr/employees',
          component: Employees,
          hidden: false,
          child: [],
        },
        {
          id: 94,
          parentId: 9,
          title: t('/signatories'),
          name: 'HR',
          pathname: 'hr/signatories',
          component: Signatories,
          hidden: false,
          child: [],
        },
        {
          id: 95,
          parentId: 9,
          title: t('/sign-groups'),
          name: 'HR',
          pathname: 'hr/sign-groups',
          component: SignGroups,
          hidden: false,
          child: [
            {
              id: 9501,
              parentId: 95,
              title: t('/sign-groups-modify'),
              name: 'HR',
              pathname: 'hr/sign-groups/modify',
              component: SignGroupModify,
              hidden: false,
              child: [],
            },
          ],
        },
      ],
    },
    {
      id: 10,
      title: t('production'),
      pathname: 'production',
      search: '?',
      name: 'PRODUCTION',
      hidden: false,
      child: [
        {
          id: 101,
          parentId: 10,
          title: t('/planning'),
          name: 'PRODUCTION',
          pathname: 'production/planning',
          component: Planning,
          hidden: false,
          child: [],
        },
        {
          id: 102,
          parentId: 10,
          title: t('/work-stations'),
          name: 'PRODUCTION',
          pathname: 'production/work-stations',
          component: WorkStations,
          hidden: false,
          child: [],
        },
        {
          id: 103,
          parentId: 10,
          title: t('/work-flow-control'),
          name: 'PRODUCTION',
          pathname: 'production/work-flow-control',
          component: WorkFlowControl,
          hidden: false,
          child: [],
        },
        {
          id: 104,
          parentId: 10,
          title: t('/orders-flow-control'),
          name: 'PRODUCTION',
          pathname: 'production/orders-flow-control',
          component: OrderFlowControl,
          hidden: false,
          child: [],
        },
      ],
    },
  ]
}
